import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import { RecordPanelMode, RecordPanelType } from './record-panel.types';

// ----------------------------------------------------------------------

const PREVIEW_PARAM = 'preview';
const PREVIEW_TYPE_PARAM = 'preview_type';
const PREVIEW_MODE_PARAM = 'preview_mode';

/**
 * URL state for the record panel (JT1-1529). The open record lives in the query string
 * (`?preview=<id>&preview_type=<entity>&preview_mode=<quick|overlay>`), so the panel is linkable,
 * browser-back closes it, and a pasted URL re-opens it over its list. Opening is a history push (so
 * back closes); stepping through prev/next replaces (so the arrows don't stack history) and preserves
 * the current mode. Other params (e.g. the active `tab`) are preserved on every change.
 */
export function useRecordPanel() {
  const [searchParams, setSearchParams] = useSearchParams();

  const previewId = searchParams.get(PREVIEW_PARAM) || undefined;
  const previewType = (searchParams.get(PREVIEW_TYPE_PARAM) as RecordPanelType | null) || undefined;
  const previewMode = (searchParams.get(PREVIEW_MODE_PARAM) as RecordPanelMode | null) || 'quick';

  const openRecord = useCallback(
    (
      type: RecordPanelType,
      id: string,
      options?: { replace?: boolean; mode?: RecordPanelMode; query?: Record<string, string | undefined> }
    ) => {
      const next = new URLSearchParams(searchParams);
      next.set(PREVIEW_PARAM, id);
      next.set(PREVIEW_TYPE_PARAM, type);
      // Only write the mode when the caller specifies one; prev/next omit it so the current mode carries.
      if (options?.mode) next.set(PREVIEW_MODE_PARAM, options.mode);
      // Extra query params to carry alongside the record — e.g. the search keyword (`q`) so the opened
      // record can highlight it (item 18). An empty/undefined value clears the param.
      if (options?.query) {
        Object.entries(options.query).forEach(([key, value]) => {
          if (value) next.set(key, value);
          else next.delete(key);
        });
      }
      setSearchParams(next, { replace: options?.replace ?? false });
    },
    [searchParams, setSearchParams]
  );

  const closeRecord = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete(PREVIEW_PARAM);
    next.delete(PREVIEW_TYPE_PARAM);
    next.delete(PREVIEW_MODE_PARAM);
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  return { previewId, previewType, previewMode, openRecord, closeRecord };
}
